// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Badge, { BadgePresentationProps, BadgeTypeValues } from "@guestsupply/MobiusOverrides/Badge/Badge";
import { mergeWithCss } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BadgeModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";

export interface ProductBadgesStyles {
    container?: InjectableCss;
    ootbBadgesWrapper?: InjectableCss;
    customBadgesWrapper?: InjectableCss;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    badgeText?: InjectableCss;
    badge?: BadgePresentationProps;
}

interface OwnProps {
    ootbBadgesAndTags: BadgeModel[];
    customBadgesAndTags: BadgeModel[];
    extendedStyles?: ProductBadgesStyles;
}

type Props = OwnProps;

export const productBadgesStyles: ProductBadgesStyles = {};

const ProductBadges: FC<Props> = ({ ootbBadgesAndTags, customBadgesAndTags, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(productBadgesStyles, extendedStyles));

    if (!ootbBadgesAndTags?.length && !customBadgesAndTags?.length) {
        return null;
    }

    const renderBadge = (badge: BadgeModel) => {
        const badgeProps =
            badge.badgeType === BadgeTypeValues.Image
                ? {
                      value: badge.largeImageBadgePath,
                      imageAltText: badge.name || badge.imageAltText,
                      imageBadgeProps: styles.badgeImageProps,
                  }
                : {
                      value: badge.displayText,
                      badgeTextColor: badge.textColorHexCode,
                      badgeBackgroundColor: badge.badgeColorHexCode,
                      badgeTextRounding: badge.badgeStyle === "Round" ? 20 : 0,
                      textBadgeCss: styles.badgeText,
                  };

        return <Badge key={badge.id} type={badge.badgeType} {...badgeProps} {...styles.badge} />;
    };

    return (
        <StyledWrapper {...styles.container}>
            {!!ootbBadgesAndTags.length && (
                <StyledWrapper {...styles.ootbBadgesWrapper}>{ootbBadgesAndTags.map(renderBadge)}</StyledWrapper>
            )}
            {!!customBadgesAndTags.length && (
                <StyledWrapper {...styles.customBadgesWrapper}>{customBadgesAndTags.map(renderBadge)}</StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default React.memo(ProductBadges);
